import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { videos, categories } from "@/mocks/videos";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCat, setActiveCat] = useState(slug || "all");
  const [animatedIn, setAnimatedIn] = useState(false);

  useEffect(() => {
    if (slug) setActiveCat(slug);
    setAnimatedIn(false);
    const timer = setTimeout(() => setAnimatedIn(true), 50);
    return () => clearTimeout(timer);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCat);
  const filteredVideos =
    activeCat === "all"
      ? videos
      : videos.filter((v) => v.category === activeCat);

  const catName = currentCategory?.name || "全部";

  return (
    <div className="pt-16">
      <div className="bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "分类" },
              { label: catName },
            ]}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3 font-heading">
            {catName}
          </h1>
          <p className="text-foreground-500 text-sm mb-8">
            共 {filteredVideos.length} 部精彩影片
          </p>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  activeCat === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 transition-all duration-500 ${
            animatedIn ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {filteredVideos.map((video) => (
            <Link
              key={video.id}
              to={`/article/${video.id}`}
              className="group cursor-pointer"
              data-product-shop
            >
              <div className="relative overflow-hidden rounded-xl mb-3">
                <img
                  src={video.coverImage}
                  alt={`${video.title} - 玫瑰视频 ${catName}`}
                  title={`${video.title} - 玫瑰视频免费在线观看`}
                  className="w-full aspect-video object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-md">
                    {video.rating}
                  </span>
                </div>
                <div className="absolute bottom-3 right-3">
                  <span className="px-2 py-0.5 bg-black/70 text-background-50 text-xs rounded-md">
                    {video.duration}
                  </span>
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-play-fill text-xl"></i>
                  </span>
                </div>
              </div>
              <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                {video.title}
              </h3>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className="text-xs text-foreground-400">{video.source}</span>
                <span className="text-xs text-foreground-300">·</span>
                <span className="text-xs text-foreground-400">
                  {video.totalEpisodes > 1 ? `${video.totalEpisodes}集` : "电影"}
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filteredVideos.length === 0 && (
          <div className="text-center py-16">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-movie-line text-2xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类下暂无影片</p>
          </div>
        )}
      </div>
    </div>
  );
}